import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TOUR_ANCHOR_ATTRIBUTE } from "./tourAnchors";
import { scheduleFrame } from "./tourDom";
import "./TourPickMode.css";

/** Marks this layer's own elements, so it never offers itself as something to pick. */
const PICK_LAYER_ATTRIBUTE = "data-tour-pick";

interface PickTarget {
  anchorId: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * One pickable rectangle per anchor id.
 *
 * Deduplicated the way the overlay resolves an anchor at run time - the first rendered element wins
 * - because a list tags its first row only in principle, but a page is free to carry the same id
 * twice and two overlapping buttons for one step would be a choice with no meaning behind it.
 */
const readTargets = (): PickTarget[] => {
  const byAnchor = new Map<string, PickTarget>();
  Array.from(document.querySelectorAll<HTMLElement>(`[${TOUR_ANCHOR_ATTRIBUTE}]`))
    .filter((element) => !element.closest(`[${PICK_LAYER_ATTRIBUTE}]`))
    .forEach((element) => {
      const anchorId = element.getAttribute(TOUR_ANCHOR_ATTRIBUTE) ?? "";
      if (!anchorId) return;
      const rect = element.getBoundingClientRect();
      const existing = byAnchor.get(anchorId);
      // jsdom and a page still laying out both report zero rects, so a zero-sized first match is
      // kept rather than dropped, and replaced as soon as a rendered one turns up.
      if (existing && (existing.width > 0 || existing.height > 0)) return;
      byAnchor.set(anchorId, {
        anchorId,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    });
  return Array.from(byAnchor.values());
};

export interface TourPickModeProps {
  /** The tour the picked anchor becomes a step on. */
  tourKey: string;
}

/**
 * Pick an element on the page for a new tour step. System admins only.
 *
 * Only elements tagged with `data-tour-anchor` can be picked, because only those ids exist in the
 * manifest - anything else would author a step against a selector the overlay could never find. Each
 * one gets a transparent button laid over it rather than a handler bolted onto the page's own
 * element: it gives the keyboard a real tab stop and Enter for free, and it leaves the page exactly
 * as it was, so the admin can still click through it to reveal the rows a row anchor needs.
 */
export const TourPickMode = ({ tourKey }: TourPickModeProps) => {
  const navigate = useNavigate();
  const [targets, setTargets] = useState<PickTarget[]>([]);
  const bannerRef = useRef<HTMLDivElement>(null);
  const firstTargetRef = useRef<HTMLButtonElement>(null);

  // Compared before it is stored, because the MutationObserver below watches the whole body and
  // this layer's own buttons are in it: a measure that always produced a new array would re-render,
  // mutate the DOM, and measure again.
  const lastRef = useRef("");
  const measure = useCallback(() => {
    const next = readTargets();
    const signature = JSON.stringify(next);
    if (signature === lastRef.current) return;
    lastRef.current = signature;
    setTargets(next);
  }, []);

  /*
    Measured a frame after mount so the page has painted, then again whenever it moves underneath:
    a scroll, a resize, or a render that adds the rows a row anchor lives on.

    Every source is coalesced onto one frame. Measuring reads a rect per anchor, which forces
    layout, and the capture-phase scroll listener fires for every scroller on the page while the
    MutationObserver fires for every batch a busy table commits - running the read on each of those
    synchronously is a forced reflow per event. The signature check below keeps the renders down;
    this keeps the reads down, which is the half that costs.
  */
  const pendingRef = useRef<(() => void) | null>(null);
  const schedule = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = scheduleFrame(() => {
      pendingRef.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    schedule();
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    const observer =
      typeof MutationObserver === "undefined" ? null : new MutationObserver(schedule);
    observer?.observe(document.body, { childList: true, subtree: true });
    return () => {
      pendingRef.current?.();
      pendingRef.current = null;
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      observer?.disconnect();
    };
  }, [schedule]);

  useEffect(() => {
    bannerRef.current?.focus();
  }, []);

  const cancel = useCallback(
    () => navigate(`/admin/tours/${encodeURIComponent(tourKey)}`),
    [navigate, tourKey]
  );

  const select = useCallback(
    (anchorId: string) =>
      navigate(
        `/admin/tours/${encodeURIComponent(tourKey)}?newStepAnchor=${encodeURIComponent(anchorId)}`
      ),
    [navigate, tourKey]
  );

  // Capture, like the tour overlay's own key handling, so a dialog on the page underneath does not
  // close on the same Escape that ends picking.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      cancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [cancel]);

  return (
    <div data-tour-pick="" data-testid="tour-pick-mode">
      {/* A region rather than an alert: this is standing instructions, not something that just
          happened, and an assertive live region that is also the focus target is read twice. */}
      <div
        className="tour-pick__banner"
        ref={bannerRef}
        tabIndex={-1}
        role="region"
        aria-label="Pick an element for this tour step"
        data-testid="tour-pick-banner"
      >
        <p className="tour-pick__banner-text">
          Click the element this step should point at. Nothing to pick? Row actions only appear when
          the page has data. Use the anchor list instead.
        </p>
        {/* The targets sit at the end of the tab order, behind every control on the page below.
            This is the keyboard's way in; from there Tab walks the rest of them. */}
        <button
          type="button"
          className="tour-pick__jump"
          disabled={targets.length === 0}
          onClick={() => firstTargetRef.current?.focus()}
          data-testid="tour-pick-jump"
        >
          Go to first element
        </button>
        <button
          type="button"
          className="tour-pick__cancel"
          onClick={cancel}
          data-testid="tour-pick-cancel"
        >
          Cancel
        </button>
      </div>

      {targets.map((target, index) => (
        <button
          key={target.anchorId}
          ref={index === 0 ? firstTargetRef : undefined}
          type="button"
          className="tour-pick__target"
          style={{
            top: target.top,
            left: target.left,
            width: target.width,
            height: target.height,
          }}
          // Named in full, because the visible label only shows on hover and focus: without this a
          // screen reader would read a bare id, and there are a dozen of them on a page.
          aria-label={`Point this step at ${target.anchorId}`}
          onClick={() => select(target.anchorId)}
          data-testid={`tour-pick-target-${target.anchorId}`}
        >
          <span className="tour-pick__target-label" aria-hidden="true">
            {target.anchorId}
          </span>
        </button>
      ))}
    </div>
  );
};
